// Copyright (c) 2026 Chain Capital Technologies Ltd. All rights reserved.
// See LICENSE file in the project root for full license information.

import { useMemo, useState } from 'preact/hooks';
import qrcode from 'qrcode-generator';
import { useSession } from '../session-context.js';

/** Public block-explorer URL for an on-chain address. Mainnet only by
 *  design: on test networks (and unknown/absent ones) this returns
 *  `null` and the address renders as plain text — there is no explorer
 *  we want to endorse there, and a wrong-network link is worse than
 *  none. */
export function mempoolAddressUrlForNetwork(
  network: string | null,
  address: string,
): string | null {
  return network === 'bitcoin' ? `https://mempool.space/address/${address}` : null;
}

/**
 * F14.10: deposit address with a QR-toggle affordance. Wallet apps
 * consume bech32 addresses upper-cased (BIP-173) — we feed the raw
 * address and rely on qrcode-generator's byte mode, which is what
 * wallets expect. Error-correction level 'L' is the most compact;
 * the address doesn't carry financial risk if the QR is damaged
 * because the user can always paste from the text row above.
 *
 * COB-355: promoted out of vault-detail.tsx into its own component so
 * the wallet and shared-vault pages stop importing a UI primitive from a
 * page module. "Deposit address" is the standard section label; pass a
 * variant only when the surrounding copy genuinely differs.
 */
export function DepositAddressField({
  address,
  label = 'Deposit address',
}: {
  // COB-245: optional because the deposit address is withheld until the owner
  // confirms the contract on the verifier. Renders nothing when absent; the
  // enclosing card shows the confirmation step instead.
  address?: string;
  /** Field label above the address. Pass `''` to omit it when an enclosing
   *  section heading already names the field. */
  label?: string;
}) {
  const session = useSession();
  const explorerUrl = address
    ? mempoolAddressUrlForNetwork(session.bitcoinNetwork, address)
    : '';
  const [showQr, setShowQr] = useState(false);
  const [copied, setCopied] = useState(false);
  const svg = useMemo(() => {
    if (!showQr || !address) return '';
    const qr = qrcode(0, 'L');
    qr.addData(address, 'Byte');
    qr.make();
    return qr.createSvgTag({ cellSize: 4, margin: 2, scalable: true });
  }, [address, showQr]);

  function handleCopy() {
    if (!address) return;
    navigator.clipboard.writeText(address).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  // COB-245: nothing to render until the address is revealed. Hooks above run
  // unconditionally (React rule); the guard sits after them.
  if (!address) return null;

  return (
    <div class="vault-field">
      {label && <div class="vault-field-label">{label}</div>}
      <div class="vault-field-value mono">
        {explorerUrl ? (
          <a class="vault-field-link" href={explorerUrl} target="_blank" rel="noopener">
            {address}
          </a>
        ) : (
          address
        )}
      </div>
      <div class="vault-field-action">
        <button
          type="button"
          class="btn btn-link"
          onClick={handleCopy}
          aria-live="polite"
        >
          {copied ? 'Copied' : 'Copy'}
        </button>
        <button
          type="button"
          class="btn btn-link"
          onClick={() => setShowQr(!showQr)}
          aria-expanded={showQr}
        >
          {showQr ? 'Hide QR code' : 'Show QR code'}
        </button>
      </div>
      {showQr && (
        <div
          class="vault-qr"
          // QR svg is generated from a trusted on-chain-address string —
          // no user input, no XSS vector.
          dangerouslySetInnerHTML={{ __html: svg }}
        />
      )}
    </div>
  );
}
